import { Check } from 'lucide-react';
import { forwardRef, useRef, type MouseEvent, type PointerEvent } from 'react';

import { CandidateAvatar } from '@/components/common/CandidateAvatar/CandidateAvatar';
import { buildAnalyticsEventName, trackAnalyticsEvent } from '@/lib/analytics';
import type { Candidate } from '@/models/candidate';
import { classNames } from '@/utils/classNames';

import styles from '@/styles/home.module.css';

type CandidateCardProps = {
  candidate: Candidate;
  selected: boolean;
  onSelect: (candidateId: string) => void;
};

export const CandidateCard = forwardRef<HTMLButtonElement, CandidateCardProps>(
  function CandidateCard({ candidate, selected, onSelect }, ref) {
    const lastPointerUpAtRef = useRef(0);
    const clickSuppressionWindowRef = useRef(500);

    const trackSelection = (): void => {
      trackAnalyticsEvent(buildAnalyticsEventName(`Selecionar ${candidate.name}`, `home:candidate:${candidate.id}`));
    };

    const handlePointerUp = (event: PointerEvent<HTMLButtonElement>): void => {
      clickSuppressionWindowRef.current = event.pointerType === 'touch' ? 800 : 500;
      lastPointerUpAtRef.current = Date.now();
      trackSelection();
      onSelect(candidate.id);
    };

    const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
      if (event.detail > 0 && Date.now() - lastPointerUpAtRef.current < clickSuppressionWindowRef.current) {
        return;
      }

      trackSelection();
      onSelect(candidate.id);
    };

    return (
      <button
        ref={ref}
        type="button"
        className={classNames(
          styles.candidateButton,
          styles.candidateCard,
          selected && styles.candidateCardActive,
        )}
        onPointerUp={handlePointerUp}
        onClick={handleClick}
        aria-pressed={selected}
        aria-label={candidate.electoralNumber ? `${candidate.name}, número ${candidate.electoralNumber}` : candidate.name}
      >
        <CandidateAvatar alt={candidate.name} imageUrl={candidate.photoUrl} name={candidate.name} size="lg" />

        <div className={styles.candidateMeta}>
          <p className={styles.candidateName}>{candidate.name}</p>
          <p className={styles.candidateParty}>{candidate.party}</p>

          {candidate.electoralNumber ? <span className={styles.candidateElectoralNumber}>{candidate.electoralNumber}</span> : null}

          {selected ? (
            <span className={styles.candidateSelectedMark}>
              <Check size={14} />
              Selecionado
            </span>
          ) : null}
        </div>
      </button>
    );
  },
);
